import { useCallback, useEffect, useState } from 'react';
import { offlineQueue } from '../services/offline-queue.service';
import { syncEngine, SyncResult } from '../services/sync-engine.service';
import { useNetworkStatus } from './useNetworkStatus';

export interface OfflineSyncState {
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
  lastResult: SyncResult | null;
  lastSyncAt: number | null;
  isOnline: boolean;
  syncNow: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useOfflineSync(): OfflineSyncState {
  const { isOnline } = useNetworkStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [p, f] = await Promise.all([
        offlineQueue.countPending(),
        offlineQueue.countFailed(),
      ]);
      setPendingCount(p);
      setFailedCount(f);
    } catch {
      // ignore
    }
  }, []);

  const syncNow = useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const result = await syncEngine.processQueue();
      setLastResult(result);
      setLastSyncAt(Date.now());
      await refresh();
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refresh]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 15000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('offline-synced', handler);
    return () => window.removeEventListener('offline-synced', handler);
  }, [refresh]);

  useEffect(() => {
    if (!isOnline) return;
    if (pendingCount === 0) return;
    const t = setTimeout(() => {
      syncNow();
    }, 1500);
    return () => clearTimeout(t);
  }, [isOnline, pendingCount, syncNow]);

  return {
    pendingCount,
    failedCount,
    isSyncing,
    lastResult,
    lastSyncAt,
    isOnline,
    syncNow,
    refresh,
  };
}